import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { getMissions } from '@/lib/api'
import { PageMotion } from '@/components/Motion'
import { ErrorBlock, LoadingBlock, PageHeader, Panel } from '@/components/ui'
import { formatNum } from '@/lib/utils'
import { Search } from 'lucide-react'

export default function Missions() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['missions', page, q],
    queryFn: () => getMissions(page, 20, q),
  })

  return (
    <PageMotion>
      <PageHeader
        title="Missions"
        subtitle={data ? `${formatNum(data.total)} recorded sessions` : 'Browse sessions'}
        action={
          <form
            className="flex gap-2 w-full sm:w-auto"
            onSubmit={(e) => {
              e.preventDefault()
              setPage(1)
              setQ(search.trim())
            }}
          >
            <div className="relative flex-1 sm:w-64">
              <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input
                className="input pl-9"
                placeholder="Search name or map…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="btn-primary">
              Search
            </button>
          </form>
        }
      />

      <Panel flush className="overflow-hidden">
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <div className="p-4">
            <ErrorBlock message="Failed to load missions" />
          </div>
        ) : (
          <>
            <div className="table-wrap border-0 rounded-none">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Map</th>
                    <th>Players</th>
                    <th>Kills</th>
                    <th>Victory</th>
                    <th>Start</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.data ?? []).map((m) => (
                    <tr key={m.id}>
                      <td className="font-mono text-xs text-ink-faint">{m.id}</td>
                      <td>
                        <Link to={`/missions/${m.id}`} className="link-accent font-medium">
                          {m.missionName || `Mission #${m.id}`}
                        </Link>
                      </td>
                      <td>{m.worldname}</td>
                      <td className="font-mono">{formatNum(m.playerCount ?? 0)}</td>
                      <td className="font-mono text-accent-text">{formatNum(m.killCount ?? 0)}</td>
                      <td>{m.victory || '—'}</td>
                      <td className="font-mono text-xs text-ink-faint">{m.missionStart || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data && data.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-line text-sm">
                <span className="text-ink-faint">
                  Page {data.page} / {data.totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={page >= data.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Panel>
    </PageMotion>
  )
}
